import axios from 'axios';
import SessionService from './SessionService';

const API_URL = process.env.REACT_APP_API_URL; // Adicionando a variável de ambiente

// Interface para representar um usuário
export interface User {
  id: number;
  name: string;
  email: string;
  phoneWhatsapp: string;
  avatarUrl: string;
  role: number; // 1=Collaborator, 2=Admin, 3=SuperAdmin, 4=PlatformAdmin
  status: boolean;
  avatar_url?: string;
  sector_id?: number;
  created_at: string;
  updated_at: string;
  sectors?: Array<{
    id: number;
    name: string;
  }>;
  invitedBy?: number | null;
  invitedByName?: string | null;
  /** Acesso ao módulo Financeiro quando true (qualquer perfil exceto PlatformAdmin). */
  financeiroAccessEnabled?: boolean;
  /** Opcional: referência externa no embed (API); não gerenciada na UI principal. */
  financeiroExternalRef?: string | null;
  /** True = workspace próprio (isolado). Default false = workspace compartilhado do tenant. */
  financeiroOwnWorkspace?: boolean;
  /** Permissão de criar (cadastros/lançamentos) no compartilhado. */
  financeiroCanCreate?: boolean;
  /** Permissão de editar no compartilhado. */
  financeiroCanEdit?: boolean;
  /** Permissão de deletar no compartilhado. */
  financeiroCanDelete?: boolean;
}

interface UserCreate {
  name: string;
  email: string;
  phoneWhatsapp: string;
  role: number; // 1=Collaborator, 2=Admin
  status: boolean;
  sectors: SectorDTO[];
  invitedBy: number;
}

interface SectorDTO {
  Id: number;
  IsShared: boolean;
}

// Interface para convite de usuário
export interface InviteToSectorDTO {
  email: string;
  name?: string;
  sectorId: number;
  role: 'Collaborator' | 'Admin' | 'SuperAdmin';
}

// Resultado do convite
export interface InviteResult {
  isSuccess: boolean;
  message: string;
  userId?: number;
  isNewUser: boolean;
}

// Interface para a atualização de um usuário
export interface UpdateUserRequestDTO {
  name: string;
  email: string;
  avatarUrl: string;
  phoneWhatsapp: string;
  role: number; // 1=Collaborator, 2=Admin
  status: boolean;
  sectors: SectorDTO[];
  invitedBy: number;
}

// Interface para representar um setor
export interface Sector {
  id: number;
  name: string;
  description?: string; // Descrição opcional do setor
}

// Serviço para criar um usuário
export const createUser = async (userData: UserCreate): Promise<User> => {
  try {
    const token = SessionService.getToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.post(`${API_URL}/users`, userData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to create user: ${error}`);
  }
};

// Serviço para verificar se email já existe
export const checkEmailExists = async (email: string): Promise<{
  exists: boolean;
  userId?: number;
  userName?: string;
  userEmail?: string;
}> => {
  try {
    const token = SessionService.getToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.get(`${API_URL}/users/check-email`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
      params: { email },
    });

    return {
      exists: response.data.exists || response.data.Exists || false,
      userId: response.data.userId || response.data.UserId,
      userName: response.data.userName || response.data.UserName,
      userEmail: response.data.userEmail || response.data.UserEmail,
    };
  } catch (error) {
    throw new Error(`Failed to check email: ${error}`);
  }
};

// Serviço para convidar usuário a um setor (novo ou existente)
export const inviteToSector = async (data: InviteToSectorDTO): Promise<InviteResult> => {
  try {
    const token = SessionService.getToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.post(`${API_URL}/users/invite`, data, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      return error.response.data;
    }
    throw new Error(`Failed to invite user: ${error}`);
  }
};

// Serviço para promover usuário a SuperAdmin (apenas SuperAdmin pode promover)
export const promoteToSuperAdmin = async (userId: number): Promise<{ message: string; userId: number; userName: string; userEmail: string }> => {
  try {
    const token = SessionService.getToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.post(`${API_URL}/users/${userId}/promote-to-superadmin`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(`Falha ao promover usuário: ${error}`);
  }
};

// Serviço para obter o role do usuário atual
export const getCurrentUserRole = async (): Promise<{
  role: string;
  roleValue: number;
  isSuperAdmin: boolean;
  canInviteUsers: boolean;
  canManageSectors: boolean;
}> => {
  try {
    const token = SessionService.getToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.get(`${API_URL}/users/role`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to get user role: ${error}`);
  }
};

// Serviço para atualizar um usuário
export const updateUser = async (id: number, data: UpdateUserRequestDTO): Promise<User> => {
  try {
    const token = SessionService.getToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.put(`${API_URL}/users/${id}`, data, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    throw new Error(`Failed to update user: ${error}`);
  }
};

// Serviço para obter um usuário pelo ID
export const getUser = async (id: number): Promise<User> => {
  try {
    const token = SessionService.getToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.get(`${API_URL}/users/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to get user with id ${id}: ${error}`);
  }
};

// Serviço para buscar todos os setores
export const getSectors = async (): Promise<Sector[]> => {
  try {
    const token = SessionService.getToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.get(`${API_URL}/sectors`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to fetch sectors: ${error}`);
  }
};

/**
 * Busca todos os usuários que compartilham setores com o usuário logado.
 * - invitedBy: filtro legado (opcional) - se não passar, lista todos dos setores compartilhados
 * - sectorId: filtra por setor específico
 */
export const getAllUsers = async (invitedBy?: number, sectorId?: number): Promise<User[]> => {
  try {
    const token = SessionService.getToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const params: any = {};
    if (typeof invitedBy === 'number') {
      params.invitedBy = invitedBy;
    }
    if (typeof sectorId === 'number') {
      params.sectorId = sectorId;
    }

    const response = await axios.get(`${API_URL}/users`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': '*/*',
      },
      params,
    });


    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return [];
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      const payload = error.response.data as { data?: unknown } | undefined;
      if (Array.isArray(payload?.data)) {
        return payload.data as User[];
      }
      return [];
    }
    throw error;
  }
};

/**
 * Atualiza a preferência de tema do usuário (light/dark/system).
 * Persiste no banco de dados.
 */
export const updateUserThemePreference = async (themePreference: 'light' | 'dark' | 'system'): Promise<void> => {
  const token = SessionService.getToken();
  if (!token) return;

  const API_URL = process.env.REACT_APP_API_URL;
  await axios.put(
    `${API_URL}/users/profile/preferences`,
    { themePreference },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
};

/**
 * Obtém a preferência de tema do usuário do servidor.
 * Retorna null se não autenticado ou em caso de erro.
 */
export const getUserThemePreference = async (): Promise<'light' | 'dark' | 'system' | null> => {
  const token = SessionService.getToken();
  if (!token) return null;

  try {
    const API_URL = process.env.REACT_APP_API_URL;
    const response = await axios.get(`${API_URL}/users/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const theme = response.data?.data?.themePreference ?? response.data?.themePreference;
    return theme && ['light', 'dark', 'system'].includes(theme) ? theme : null;
  } catch {
    return null;
  }
};

// Serviço para deletar um usuário
export const deleteUser = async (id: string): Promise<void> => {
  try {
    const token = SessionService.getToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    await axios.delete(`${API_URL}/users/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  } catch (error) {
    throw new Error(`Failed to delete user: ${error}`);
  }
};

/** SuperAdmin: atualiza referência opcional no embed (uso avançado). */
export const setMyFinanceiroExternalRef = async (externalRef: string | null): Promise<void> => {
  const token = SessionService.getToken();
  if (!token) {
    throw new Error('Token de autenticação não encontrado');
  }

  await axios.put(
    `${API_URL}/users/profile/financeiro-external-ref`,
    { externalRef },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
};

export const updateUserSectorRole = async (
  userId: number,
  sectorId: number,
  roleNoSetor: number
): Promise<{ message: string }> => {
  const token = SessionService.getToken();
  if (!token) {
    throw new Error('Token de autenticação não encontrado');
  }

  const response = await axios.put(
    `${API_URL}/users/${userId}/sectors/${sectorId}/role`,
    { roleNoSetor },
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  return response.data;
};
